import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';

import { useSetupAmplitude } from './useSetupAmplitude';
import { useSignupParams } from './useSignupParams';
import StaffErrorBanner from '../components/StaffErrorBanner/StaffErrorBanner';
import { ThreatMetrix } from '../components/ThreatMetrix/ThreatMetrix';
import {
  useGetApplicationQuery,
  useGetBrandingQuery,
  useGetContext,
  useGetProfile,
  useGetStateQuery,
} from '../lendingApi/lendingApiQueries';

const SignupContext = createContext<
  | {
      isLoading: boolean;
      setIsLoading: Dispatch<SetStateAction<boolean>>;
      hasAllData: boolean;
    }
  | undefined
>(undefined);

type TProps = {
  children: React.ReactNode;
};

export const SignupProvider = (props: TProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { isBrandingLoading, branding } = useGetBrandingQuery();
  const { isApplicationLoading, application } = useGetApplicationQuery();
  const { isStateLoading, state } = useGetStateQuery();
  const { isProfileLoading, profile } = useGetProfile();
  const { isContextLoading, context } = useGetContext();

  const { isDirectCustomer } = useSignupParams();

  const isApiLoading =
    isProfileLoading ||
    isContextLoading ||
    isStateLoading ||
    isApplicationLoading ||
    (!isDirectCustomer && isBrandingLoading);

  const hasAllData = Boolean(
    (isDirectCustomer || branding) &&
      application &&
      state &&
      context &&
      profile,
  );

  useSetupAmplitude();

  return (
    <SignupContext.Provider
      value={{
        isLoading: isApiLoading || isLoading,
        setIsLoading,
        hasAllData,
      }}
      {...props}
    >
      <StaffErrorBanner />
      {props.children}
      <ThreatMetrix />
    </SignupContext.Provider>
  );
};

export function useSignup() {
  const context = useContext(SignupContext);
  if (context === undefined) {
    throw new Error('useSignup must be used within the SignupProvider');
  }
  return context;
}
